import {
  WALK_THRESHOLD_KM,
  WALK_SPEED_KMH,
  DRIVE_SPEED_KMH,
  TRANSIT_SPEED_KMH,
  TRANSIT_OVERHEAD_MIN,
  MAX_REASONABLE_DRIVE_KM,
  DEFAULT_TRIP_TRAVEL_MODE,
  type TravelMode,
  type TripDefaultTravelMode,
} from "@poi-planner/shared";

export interface Coords {
  lat: number;
  lng: number;
}

export interface TravelEstimate {
  distanceKm: number;
  distanceMiles: number;
  travelMinutes: number;
  mode: TravelMode;
  icon: string;
}

const EARTH_RADIUS_KM = 6371;
const KM_TO_MILES = 0.621371;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function haversineDistanceKm(a: Coords, b: Coords): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h =
    sinLat * sinLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/** A trip's preferences for automatically chosen travel modes. */
export interface TravelModePreferences {
  /** Mode for hops too long to walk */
  defaultTravelMode: TripDefaultTravelMode;
  /** Longest hop walked automatically, in km; 0 never walks */
  walkThresholdKm: number;
}

export const DEFAULT_TRAVEL_MODE_PREFERENCES: TravelModePreferences = {
  defaultTravelMode: DEFAULT_TRIP_TRAVEL_MODE,
  walkThresholdKm: WALK_THRESHOLD_KM,
};

/** Preferences for a trip, falling back to the defaults for unset fields. */
export function tripTravelModePreferences(
  trip:
    | { defaultTravelMode?: TripDefaultTravelMode; walkThresholdKm?: number }
    | null
    | undefined,
): TravelModePreferences {
  return {
    defaultTravelMode:
      trip?.defaultTravelMode ??
      DEFAULT_TRAVEL_MODE_PREFERENCES.defaultTravelMode,
    walkThresholdKm:
      trip?.walkThresholdKm ?? DEFAULT_TRAVEL_MODE_PREFERENCES.walkThresholdKm,
  };
}

/**
 * Automatic mode for a straight-line distance: walk hops up to the trip's
 * threshold, otherwise the trip's default mode.
 */
export function autoTravelMode(
  distanceKm: number,
  preferences: TravelModePreferences = DEFAULT_TRAVEL_MODE_PREFERENCES,
): TravelMode {
  return distanceKm <= preferences.walkThresholdKm &&
    preferences.walkThresholdKm > 0
    ? "walk"
    : preferences.defaultTravelMode;
}

export const TRAVEL_MODE_ICONS: Record<TravelMode, string> = {
  walk: "directions_walk",
  transit: "directions_transit",
  drive: "directions_car",
};

export const TRAVEL_MODE_LABELS: Record<TravelMode, string> = {
  walk: "Walk",
  transit: "Transit",
  drive: "Drive",
};

function estimatedMinutes(distanceKm: number, mode: TravelMode): number {
  switch (mode) {
    case "walk":
      return (distanceKm / WALK_SPEED_KMH) * 60;
    case "transit":
      return (distanceKm / TRANSIT_SPEED_KMH) * 60 + TRANSIT_OVERHEAD_MIN;
    case "drive":
      return (distanceKm / DRIVE_SPEED_KMH) * 60;
  }
}

/**
 * Offline straight-line travel estimate. `mode` defaults to the automatic
 * choice for the distance. Returns null beyond a reasonable ground distance.
 */
export function estimateTravel(
  a: Coords,
  b: Coords,
  mode?: TravelMode,
): TravelEstimate | null {
  const distanceKm = haversineDistanceKm(a, b);

  // Return null for unreasonably long distances (would need a flight/train, not driving)
  if (distanceKm > MAX_REASONABLE_DRIVE_KM) {
    return null;
  }

  const resolvedMode = mode ?? autoTravelMode(distanceKm);
  return {
    distanceKm,
    distanceMiles: distanceKm * KM_TO_MILES,
    travelMinutes: Math.max(
      1,
      Math.ceil(estimatedMinutes(distanceKm, resolvedMode)),
    ),
    mode: resolvedMode,
    icon: TRAVEL_MODE_ICONS[resolvedMode],
  };
}

export function formatTravelEstimate(est: TravelEstimate): string {
  const miles =
    est.distanceMiles < 1
      ? `${est.distanceMiles.toFixed(1)} mi`
      : `${Math.round(est.distanceMiles)} mi`;
  return `${est.travelMinutes} min (${miles})`;
}
